// ISO-week helpers for release-notes admin.
// Implementation copied from a7y-webapp/src/marketing/pages/ReleaseNotesPage.tsx
// (lines 87-131) so that the two surfaces share identical grouping logic
// without cross-workspace imports.

/** Returns an ISO-week key like "2026-W15" for grouping */
export function getISOWeekKey(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  const dayOfWeek = d.getDay() || 7; // Monday=1 ... Sunday=7
  d.setDate(d.getDate() + 4 - dayOfWeek); // Thursday of the week
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const weekNum = Math.ceil(
    ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
  );
  return `${d.getFullYear()}-W${String(weekNum).padStart(2, "0")}`;
}

function toISODateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Returns the Monday and Sunday bounding the ISO week that contains dateStr,
 * as YYYY-MM-DD strings for use in admin API date-range params.
 */
export function weekDateRange(dateStr: string): {
  startDate: string;
  endDate: string;
} {
  const d = new Date(dateStr + "T00:00:00");
  const dayOfWeek = d.getDay() || 7;
  const monday = new Date(d);
  monday.setDate(d.getDate() - dayOfWeek + 1);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { startDate: toISODateString(monday), endDate: toISODateString(sunday) };
}

/**
 * Format a week date range for display, e.g.
 * "Mar 10 - 16, 2026" or "Mar 28 - Apr 3, 2026"
 * Accepts YYYY-MM-DD strings (the shape returned by weekDateRange and by the
 * /weeks endpoint).
 */
export function formatWeekRange(
  startDate: string,
  endDate: string,
): string {
  const start = new Date(startDate + "T00:00:00");
  const end = new Date(endDate + "T00:00:00");

  const sameMonth =
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear();

  const monthShort = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short" });

  if (sameMonth) {
    return `${monthShort(start)} ${start.getDate()} \u2013 ${end.getDate()}, ${end.getFullYear()}`;
  }

  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameYear) {
    return `${monthShort(start)} ${start.getDate()} \u2013 ${monthShort(end)} ${end.getDate()}, ${end.getFullYear()}`;
  }

  return `${monthShort(start)} ${start.getDate()}, ${start.getFullYear()} \u2013 ${monthShort(end)} ${end.getDate()}, ${end.getFullYear()}`;
}

/** Pretty date formatter for per-doc (YYYY-MM-DD) headers. */
export function formatEntryDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
